import * as React from 'react';
import { meetingsApi } from '../services/meetingsApi';

type SyncState = 'idle' | 'syncing' | 'done' | 'error';

/**
 * Provides a `sync()` function that calls POST /calendar/sync/me.
 * Returns the sync state and any error message.
 * After a successful sync the state transitions to 'done' briefly, then
 * auto-resets to 'idle' after 2 seconds.
 */
export function useCalendarSync() {
  const [syncState, setSyncState] = React.useState<SyncState>('idle');
  const [error, setError] = React.useState<string | null>(null);
  const doneTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const sync = React.useCallback(async () => {
    if (doneTimerRef.current) {
      clearTimeout(doneTimerRef.current);
      doneTimerRef.current = null;
    }
    setSyncState('syncing');
    setError(null);
    try {
      await meetingsApi.syncMe();
      setSyncState('done');
      doneTimerRef.current = setTimeout(() => setSyncState('idle'), 2000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      setError(message);
      setSyncState('error');
    }
  }, []);

  const reset = React.useCallback(() => {
    if (doneTimerRef.current) {
      clearTimeout(doneTimerRef.current);
      doneTimerRef.current = null;
    }
    setSyncState('idle');
    setError(null);
  }, []);

  React.useEffect(() => {
    return () => {
      if (doneTimerRef.current) clearTimeout(doneTimerRef.current);
    };
  }, []);

  return {
    syncing: syncState === 'syncing',
    syncDone: syncState === 'done',
    syncError: error,
    sync,
    reset,
  };
}
